<script lang="ts">
export default { name: 'App' };
</script>

<script lang="ts" setup>
import { onMounted, ref, watch, computed, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';

// COMPOSABLE
  import { useSessionBroadcast } from '@/shared/common/composables/useSessionBroadcast';
  import { useTokenRefresh } from '@/shared/common/composables/useTokenRefresh';
  import { useStaleLoaderGuard } from '@/shared/common/composables/useStaleLoaderGuard';
import { useSentryUser } from '@/core/sentry';
import { useRemoteConfigStore, useMaintenance } from '@/shared/firebase';
import { useAccountStore } from '@/stores/account';

//COMPONENTS
import { Loader as GLoader, useLoader } from '@flash-global66/g-loader';
import { GConfigProvider } from '@flash-global66/g-config-provider';
import { PinCreationModal } from './shared/pin';
import SessionExpirationAlert from '@/shared/common/components/SessionExpirationAlert/index.vue';
import CreateWalletFlow from '@/modules/wallet/sections/CreateWalletFlow/index.vue';

//HELPERS
import { isOnboardingCompleted } from '@/helpers/kyc';
import { maintenance, session } from '@/helpers/auth';
import { showHotjarWidget } from '@/helpers/hotjarWidget';

const { initUserWatcher } = useSentryUser();
initUserWatcher();

const remoteConfigStore = useRemoteConfigStore();
const { isMaintenanceMode } = useMaintenance();

const showAskPinModal = ref(false);
const route = useRoute();
const router = useRouter();

const accountStore = useAccountStore();
const { showLoader, loaderMessage } = useLoader();

const kycStatus = computed(() => accountStore.kycStatus);

useSessionBroadcast();
useStaleLoaderGuard();
const { showExpirationAlert, timeRemaining, extendSession, closeSession } =
  useTokenRefresh();

watch(
  kycStatus,
  (kyc) => {
    if (session() && isOnboardingCompleted(kyc)) {
      showAskPinModal.value = !kyc?.b2bUser?.hasPin;
    }
  },
  {
    deep: true,
    immediate: true,
  }
);

let removeAfterEach: (() => void) | null = null;

onMounted(() => {
  removeAfterEach = router.afterEach(() => {
    showHotjarWidget();
  });
});

onMounted(async () => {
  await remoteConfigStore.initialize();
});

watch(isMaintenanceMode, (newValue) => {
  if (newValue) {
    router.push(maintenance());
  }
}, { immediate: true });

onBeforeUnmount(() => {
  if (removeAfterEach) {
    removeAfterEach();
  }
  remoteConfigStore.stopRealtimeUpdates();
});
</script>

<template>
  <main>
    <GLoader :show="showLoader" :message="loaderMessage" />
    <PinCreationModal v-model="showAskPinModal" />
    <GConfigProvider>
      <CreateWalletFlow />
      <SessionExpirationAlert
        :show="showExpirationAlert"
        :time-remaining="timeRemaining"
        @extend-session="extendSession"
        @close-session="closeSession"
      />
      <RouterView :key="route.path" />
    </GConfigProvider>
  </main>
</template>

<style lang="scss">
@use './assets/styles/ui-framework-b2b/index.scss' as *;

:root {
  --height-header-navbar: 85px;
}
</style>
